import type { Candle, ChartInterval, DerivativesSnapshot, MarketSnapshot } from '../../types/market';
import { safeNumber } from '../../lib/number';
import { fetchFirstJson, fetchJson } from './http';

const SPOT_APIS = ['https://data-api.binance.vision', 'https://api.binance.com', 'https://api1.binance.com'];
const FUTURES_API = 'https://fapi.binance.com';

interface BinanceKline extends Array<number | string> {
  0: number;
  1: string;
  2: string;
  3: string;
  4: string;
  5: string;
  6: number;
  7: string;
  8: number;
}

interface BinanceTicker24h {
  symbol: string;
  lastPrice: string;
  openPrice: string;
  highPrice: string;
  lowPrice: string;
  volume: string;
  quoteVolume: string;
  bidPrice?: string;
  askPrice?: string;
  closeTime: number;
}

interface BinancePremiumIndex {
  symbol: string;
  markPrice: string;
  indexPrice: string;
  lastFundingRate: string;
  nextFundingTime: number;
  time: number;
}

interface BinanceOpenInterest {
  symbol: string;
  openInterest: string;
  time: number;
}

export async function getBinanceServerOffset(): Promise<number> {
  const result = await fetchFirstJson<{ serverTime: number }>(SPOT_APIS.map((base) => `${base}/api/v3/time`));
  return result.serverTime - Date.now();
}

export async function getBinanceCandles(
  symbol: string,
  interval: ChartInterval,
  limit = 300,
  marketKind: 'spot' | 'futures' = 'spot',
  serverNow = Date.now(),
): Promise<Candle[]> {
  const path = marketKind === 'futures' ? '/fapi/v1/klines' : '/api/v3/klines';
  const query = `${path}?symbol=${encodeURIComponent(symbol)}&interval=${encodeURIComponent(interval)}&limit=${Math.min(limit, 1_000)}`;
  const rows = marketKind === 'futures'
    ? await fetchJson<BinanceKline[]>(`${FUTURES_API}${query}`)
    : await fetchFirstJson<BinanceKline[]>(SPOT_APIS.map((base) => `${base}${query}`));
  return rows.map((row) => ({
    openTime: safeNumber(row[0]),
    open: safeNumber(row[1]),
    high: safeNumber(row[2]),
    low: safeNumber(row[3]),
    close: safeNumber(row[4]),
    volume: safeNumber(row[5]),
    closeTime: safeNumber(row[6]),
    quoteVolume: safeNumber(row[7]),
    trades: safeNumber(row[8]),
    closed: safeNumber(row[6]) < serverNow,
  }));
}

export async function getBinanceTicker(
  symbol: string,
  marketKind: 'spot' | 'futures',
): Promise<MarketSnapshot> {
  const path = marketKind === 'futures' ? '/fapi/v1/ticker/24hr' : '/api/v3/ticker/24hr';
  const query = `${path}?symbol=${encodeURIComponent(symbol)}`;
  const result = marketKind === 'futures'
    ? await fetchJson<BinanceTicker24h>(`${FUTURES_API}${query}`)
    : await fetchFirstJson<BinanceTicker24h>(SPOT_APIS.map((base) => `${base}${query}`));
  const baseAsset = symbol.endsWith('USDT') ? symbol.slice(0, -4) : symbol;
  return {
    symbol,
    baseAsset,
    quoteAsset: 'USDT',
    exchange: 'binance',
    marketKind,
    price: safeNumber(result.lastPrice),
    open24h: safeNumber(result.openPrice),
    high24h: safeNumber(result.highPrice),
    low24h: safeNumber(result.lowPrice),
    baseVolume24h: safeNumber(result.volume),
    quoteVolume24h: safeNumber(result.quoteVolume),
    bid: result.bidPrice ? safeNumber(result.bidPrice) : undefined,
    ask: result.askPrice ? safeNumber(result.askPrice) : undefined,
    eventTime: result.closeTime,
  };
}

function fundingBehavior(rate?: number): DerivativesSnapshot['fundingBehavior'] {
  if (rate === undefined) return 'unavailable';
  if (rate >= 0.001) return 'strongly-positive';
  if (rate > 0.0001) return 'positive';
  if (rate <= -0.001) return 'strongly-negative';
  if (rate < -0.0001) return 'negative';
  return 'neutral';
}

export async function getBinanceDerivatives(symbol: string): Promise<DerivativesSnapshot> {
  const [premium, openInterest, futuresTicker] = await Promise.allSettled([
    fetchJson<BinancePremiumIndex>(`${FUTURES_API}/fapi/v1/premiumIndex?symbol=${encodeURIComponent(symbol)}`),
    fetchJson<BinanceOpenInterest>(`${FUTURES_API}/fapi/v1/openInterest?symbol=${encodeURIComponent(symbol)}`),
    fetchJson<BinanceTicker24h>(`${FUTURES_API}/fapi/v1/ticker/24hr?symbol=${encodeURIComponent(symbol)}`),
  ]);

  const premiumValue = premium.status === 'fulfilled' ? premium.value : undefined;
  const oiValue = openInterest.status === 'fulfilled' ? openInterest.value : undefined;
  const tickerValue = futuresTicker.status === 'fulfilled' ? futuresTicker.value : undefined;
  const fundingRate = premiumValue ? safeNumber(premiumValue.lastFundingRate) : undefined;

  return {
    fundingRate,
    fundingBehavior: fundingBehavior(fundingRate),
    openInterest: oiValue ? safeNumber(oiValue.openInterest) * (premiumValue ? safeNumber(premiumValue.markPrice) : 1) : undefined,
    futuresVolume24h: tickerValue ? safeNumber(tickerValue.quoteVolume) : undefined,
    markPrice: premiumValue ? safeNumber(premiumValue.markPrice) : undefined,
    indexPrice: premiumValue ? safeNumber(premiumValue.indexPrice) : undefined,
    source: 'Binance Futures public market data',
  };
}
